import { alpha, createTheme, type Theme } from '@mui/material/styles';

// Thème MUI global du POC : source unique des couleurs et de la police. Les composants utilisent les clés du thème
// (sx : 'primary.main', 'text.secondary', 'divider', 'badge.green.bg'… ; code : useTheme()) et jamais de code
// hexadécimal. Valeurs reprises de celles qui étaient codées en dur (palette « Skote » : accent indigo, gris Bootstrap).

export const DEFAULT_PRIMARY = '#556ee6';

export const FONT_FAMILY = "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif";

// Police à chasse fixe (n° de carte, identifiants, code)
export const MONO_FONT_FAMILY = "'SFMono-Regular', Menlo, Consolas, 'Liberation Mono', monospace";

export type ChannelPaletteKey = 'courrier' | 'email' | 'sms' | 'wallet' | 'cashdesk';

interface BadgeColor {
  bg: string;
  fg: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    // Pastilles de statut : fond clair + texte foncé
    badge: Record<'blue' | 'green' | 'orange' | 'purple' | 'red' | 'pink' | 'grey', BadgeColor>;
    // Une couleur d'identité par canal de campagne, la même dans tous les écrans (calendrier, création, ciblage…)
    channel: Record<ChannelPaletteKey, string>;
    // Palette catégorielle (séries de graphiques, avatars de badges) : couleurs distinctes, dans l'ordre
    chart: string[];
    // Couleurs propres au layout (menu latéral sombre)
    layout: { sidebarDark: string; sidebarDarkText: string };
  }
  interface PaletteOptions {
    badge?: Palette['badge'];
    channel?: Palette['channel'];
    chart?: Palette['chart'];
    layout?: Palette['layout'];
  }
  interface TypographyVariants {
    fontFamilyMono: string;
  }
  interface TypographyVariantsOptions {
    fontFamilyMono?: string;
  }
}

export const buildTheme = (primary: string = DEFAULT_PRIMARY): Theme => createTheme({
  palette: {
    // Couleur d'accent choisie par l'utilisateur dans les paramètres (LayoutPreferences.sidebarColor)
    primary: { main: primary, contrastText: '#ffffff' },
    secondary: { main: '#74788d' },
    error: { main: '#dc3545' },
    success: { main: '#2e7d32' },
    warning: { main: '#ed6c02' },
    info: { main: '#0288d1' },
    // Échelle de gris Bootstrap (utilisée partout dans l'app)
    grey: {
      50: '#f8f9fa',
      100: '#f1f3f5',
      200: '#e9ecef',
      300: '#dee2e6',
      400: '#ced4da',
      500: '#adb5bd',
      600: '#6c757d',
      700: '#495057',
      800: '#343a40',
      900: '#212529',
    },
    text: {
      primary: '#495057',
      secondary: '#6c757d',
      disabled: '#adb5bd',
    },
    divider: '#dee2e6',
    background: {
      default: '#f3f3f9',
      paper: '#ffffff',
    },
    badge: {
      blue: { bg: '#e3f2fd', fg: '#1565c0' },
      green: { bg: '#e8f5e9', fg: '#2e7d32' },
      orange: { bg: '#fff3e0', fg: '#e65100' },
      purple: { bg: '#f3e5f5', fg: '#6a1b9a' },
      red: { bg: '#ffebee', fg: '#c62828' },
      pink: { bg: '#fce4ec', fg: '#c2185b' },
      grey: { bg: '#f1f3f5', fg: '#495057' },
    },
    // Valeurs en hexadécimal à 6 chiffres : certains écrans y ajoutent un suffixe d'opacité
    channel: {
      courrier: '#fd7e14',
      email: '#0d6efd',
      sms: '#6f42c1',
      wallet: '#20c997',
      cashdesk: '#e83e8c',
    },
    chart: ['#5c6bc0', '#26a69a', '#ef5350', '#ab47bc', '#ff7043', '#29b6f6', '#66bb6a', '#ffa726'],
    layout: {
      sidebarDark: '#2a3042',
      sidebarDarkText: '#a6b0cf',
    },
  },
  typography: {
    fontFamily: FONT_FAMILY,
    fontFamilyMono: MONO_FONT_FAMILY,
  },
});

// Fond teinté de la couleur principale (survol, sélection). Remplace les suffixes hexadécimaux du type `${sidebarColor}15`.
export const primarySoft = (theme: Theme, opacity = 0.08): string => alpha(theme.palette.primary.main, opacity);
